// engine-exception: the impact × urgency matrix is a grid whose cells upsert priority_matrix rows keyed by (impact, urgency) — a lookup table, not one record's form
import { useEffect, useMemo, useState } from 'react';
import { useCreate, useRemove, useResourceList, useUpdate } from '../../engine/api/hooks';
import { Badge, ErrorBanner, PageHeader, Skeleton } from '../../engine/design/primitives/Misc';
import { Button } from '../../engine/design/primitives/Button';
import { Field, Select } from '../../engine/design/primitives/Input';
import { priorityTone, useImpacts, usePriorities, useUrgencies } from './lib/catalog';

// Priority matrix editor (ServiceNow-style lookup): rows = impact, columns = urgency, each cell
// picks the priority a ticket gets from that pair. The tenant-wide matrix is the base; a ticket
// type PROFILE may override any cell (priority_matrix.profile_id) — a cell left blank on a
// profile inherits the tenant-wide value, so an override is exactly the cells that differ.

type Row = Record<string, unknown>;
interface ProfileRow { id: string; name: string; priority_mode: 'matrix' | 'direct' }

export function PriorityMatrixScreen() {
  const impacts = useImpacts();
  const urgencies = useUrgencies();
  const priorities = usePriorities();
  const profiles = useResourceList<ProfileRow>('ticket-type-profiles', { sort: 'sort' }, { limit: 100 });
  const [profileId, setProfileId] = useState<string>('');
  const matrix = useResourceList<Row>('priority-matrix', {}, { limit: 500 });
  const create = useCreate('priority-matrix');
  const update = useUpdate('priority-matrix');
  const remove = useRemove('priority-matrix');
  const [cells, setCells] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const keyOf = (m: Row) => `${String(m['impact_id'])}:${String(m['urgency_id'])}`;
  // Tenant-wide cells (the base) and the selected profile's own cells (the overrides).
  const base = useMemo(() => new Map(matrix.rows.filter((m) => !m['profile_id']).map((m) => [keyOf(m), m])), [matrix.rows]);
  const own = useMemo(() => new Map(matrix.rows.filter((m) => (profileId ? m['profile_id'] === profileId : !m['profile_id'])).map((m) => [keyOf(m), m])), [matrix.rows, profileId]);
  useEffect(() => {
    const c: Record<string, string> = {};
    for (const [k, m] of own) c[k] = String(m['priority_id']);
    setCells(c);
  }, [own]);

  const save = async () => {
    setSaving(true);
    try {
      const keys = new Set([...Object.keys(cells), ...own.keys()]);
      for (const key of keys) {
        const priorityId = cells[key] ?? '';
        const row = own.get(key);
        const [impact_id, urgency_id] = key.split(':') as [string, string];
        if (!priorityId) {
          // A profile cell cleared = inherit again; a tenant cell cannot be blank (leave it).
          if (row && profileId) await remove.mutateAsync(String(row['id']));
          continue;
        }
        if (row) {
          if (String(row['priority_id']) !== priorityId) await update.mutateAsync({ id: String(row['id']), body: { priority_id: priorityId } });
        } else await create.mutateAsync({ impact_id, urgency_id, priority_id: priorityId, ...(profileId ? { profile_id: profileId } : {}) });
      }
      matrix.refetch();
    } finally {
      setSaving(false);
    }
  };

  if (impacts.error || urgencies.error || matrix.error) return <ErrorBanner error={impacts.error ?? urgencies.error ?? matrix.error} />;
  const loading = impacts.isLoading || urgencies.isLoading || priorities.isLoading || matrix.isLoading;
  const matrixProfiles = profiles.rows.filter((p) => p.priority_mode === 'matrix');
  const overrides = profileId ? own.size : 0;
  return (
    <div className="hf-page narrow">
      <PageHeader
        compact
        title="Priority matrix"
        subtitle={profileId ? `Overrides for this profile — a blank cell inherits the tenant-wide value (${overrides} overridden).` : 'Impact × urgency → priority, for every type in matrix mode. Profiles can override single cells.'}
        actions={
          <div className="hf-row">
            {matrixProfiles.length > 0 && (
              <Field label="Scope" id="pm-scope">
                <Select id="pm-scope" size="sm" value={profileId} onChange={(e) => setProfileId(e.target.value)} options={[{ value: '', label: 'Tenant-wide (default)' }, ...matrixProfiles.map((p) => ({ value: p.id, label: `Profile: ${p.name}` }))]} />
              </Field>
            )}
            <Button variant="primary" icon="save" busy={saving} onClick={() => void save()}>Save matrix</Button>
          </div>
        }
      />
      {loading ? <Skeleton lines={5} /> : (
        <table className="hf-table" aria-label="Priority matrix">
          <thead>
            <tr>
              <th>Impact ↓ / Urgency →</th>
              {urgencies.rows.map((u) => <th key={u.id}>{u.name}</th>)}
            </tr>
          </thead>
          <tbody>
            {impacts.rows.map((i) => (
              <tr key={i.id}>
                <td><strong>{i.name}</strong><div className="hf-muted" style={{ fontSize: 'var(--t-11)' }}>{i.description ?? ''}</div></td>
                {urgencies.rows.map((u) => {
                  const key = `${i.id}:${u.id}`;
                  const inherited = profileId ? base.get(key) : undefined;
                  const shown = cells[key] ?? '';
                  const p = priorities.byId.get(shown || String(inherited?.['priority_id'] ?? ''));
                  return (
                    <td key={u.id}>
                      <div className="hf-row">
                        <Select size="sm" value={shown} placeholder={inherited ? `Inherit (${priorities.byId.get(String(inherited['priority_id']))?.name ?? '—'})` : '—'} aria-label={`${i.name} impact, ${u.name} urgency`} options={priorities.rows.map((pr) => ({ value: pr.id, label: pr.name }))} onChange={(e) => setCells({ ...cells, [key]: e.target.value })} />
                        {p && <Badge tone={priorityTone(p.level)} dot>{p.key.toUpperCase()}</Badge>}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
